import type { Reference } from '@medplum/fhirtypes'
import type { ExtendedObservation } from '#src/models/observation.ts'
import { observationAnswerSetId } from '#src/observationReferences.ts'

// One answer as recorded: a single Observation, or the several Observations
// of one multi-select answer (one per chosen value).
export interface ObservationCapture {
  // The answer set's id, or the first member's id for one recorded alone.
  key: string
  members: ExtendedObservation[]
  // The chosen values' displays, in the order they were recorded.
  values: string[]
  effectiveDateTime: string | undefined
  performer: Reference | undefined
  status: string
}

// Records of one capture made before answer sets were tied together share
// their code and performer and were made within this long of each other.
const SAME_CAPTURE_MS = 60_000

// Observations (any order) as captures, newest first: by their answer-set
// identifier, else, for older records, by code, performer and time.
export function groupObservationCaptures(
  observations: readonly ExtendedObservation[]
): ObservationCapture[] {
  const newestFirst = [...observations].sort((a, b) =>
    (b.effectiveDateTime ?? '').localeCompare(a.effectiveDateTime ?? '')
  )
  const captures: ObservationCapture[] = []
  const bySet = new Map<string, ObservationCapture>()
  for (const observation of newestFirst) {
    const setId = observationAnswerSetId(observation.identifiers())
    const known = setId
      ? bySet.get(setId)
      : fallbackCapture(captures, observation)
    if (known) {
      known.members.push(observation)
      continue
    }
    const capture: ObservationCapture = {
      key: setId ?? observation.id ?? String(captures.length),
      members: [],
      values: [],
      effectiveDateTime: observation.effectiveDateTime,
      performer: observation.toFhirResource().performer?.[0],
      status: observation.toFhirResource().status,
    }
    capture.members.push(observation)
    captures.push(capture)
    if (setId) {
      bySet.set(setId, capture)
    }
  }
  // Within a capture, oldest first; records made together keep the order
  // they were given in.
  const order = new Map(
    observations.map((observation, at) => [observation, at])
  )
  for (const capture of captures) {
    capture.members.sort(
      (a, b) =>
        (a.effectiveDateTime ?? '').localeCompare(b.effectiveDateTime ?? '') ||
        (order.get(a) ?? 0) - (order.get(b) ?? 0)
    )
    capture.values = capture.members.flatMap((member) =>
      member.valueDisplay ? [member.valueDisplay] : []
    )
  }
  return captures
}

// The capture an older record (no answer-set id) belongs to: one without an
// id either, of the same code and performer, made within a minute of it.
// Only coded answers group this way, each value once: a multi-select answer
// is several chosen values, while two readings of a quantity, or the same
// answer twice, are separate captures.
function fallbackCapture(
  captures: readonly ObservationCapture[],
  observation: ExtendedObservation
): ObservationCapture | undefined {
  if (observationAnswerSetId(observation.identifiers())) {
    return undefined
  }
  const answer = answerCode(observation)
  if (!answer) {
    return undefined
  }
  const code = (candidate: ExtendedObservation) => {
    const coding = candidate.toFhirResource().code.coding?.[0]
    return `${coding?.system ?? ''}|${coding?.code ?? ''}`
  }
  const performer = (candidate: ExtendedObservation) =>
    candidate.toFhirResource().performer?.[0]?.reference
  const time = (value: string | undefined) =>
    value ? Date.parse(value) : Number.NaN
  return captures.find((capture) => {
    const first = capture.members[0]
    return (
      !!first &&
      !observationAnswerSetId(first.identifiers()) &&
      code(first) === code(observation) &&
      performer(first) === performer(observation) &&
      Math.abs(
        time(first.effectiveDateTime) - time(observation.effectiveDateTime)
      ) <= SAME_CAPTURE_MS &&
      capture.members.every((member) => {
        const chosen = answerCode(member)
        return chosen !== undefined && chosen !== answer
      })
    )
  })
}

// A coded answer's value, as system|code; none for any other value.
function answerCode(observation: ExtendedObservation): string | undefined {
  const coding = observation.toFhirResource().valueCodeableConcept?.coding?.[0]
  return coding?.code ? `${coding.system ?? ''}|${coding.code}` : undefined
}
